import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRight, Briefcase, ChevronsUpDown, Clock, Search, Star } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import type { ProjectDetails } from '@/types';
import { useProjectSelector } from '@/hooks/use-project-selector';
import { ProjectSearchModal } from '@/components/booking/ProjectSearchModal';

/** Value used by the filters to match bookings without a project. */
export const NO_PROJECT_VALUE = 'no-project';

const SEARCH_DEBOUNCE_MS = 250;
const MAX_VISIBLE_RESULTS = 50;
const MAX_VISIBLE_CHIPS = 2;
const MAX_COLLAPSED_SELECTED = 5;

interface ProjectFilterSelectProps {
  /** Candidate projects, already narrowed by any status filter. */
  projects: ProjectDetails[];
  value: string[];
  onValueChange: (value: string[]) => void;
  placeholder?: string;
  /** Statuses currently allowed by the filter drawer, passed on to the browse dialog. */
  statuses?: string[];
}

interface SearchEntry {
  id: string;
  label: string;
  clientName: string;
  haystack: string;
}

function projectLabel(project: ProjectDetails): string {
  return project.name && project.billingCode
    ? `${project.name} (${project.billingCode})`
    : project.name || String(project.id);
}

export function ProjectFilterSelect({
  projects,
  value,
  onValueChange,
  placeholder = 'All Projects',
  statuses,
}: ProjectFilterSelectProps) {
  const [open, setOpen] = useState(false);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [showAllSelected, setShowAllSelected] = useState(false);

  const {
    enrichedProjects,
    favouriteProjects,
    recentProjects,
    toggleFavourite,
    recordProjectAccess,
    isFavourite,
  } = useProjectSelector(projects);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim().toLowerCase()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebouncedQuery('');
      setShowAllSelected(false);
    }
  }, [open]);

  // Lower-cased search index so filtering thousands of projects stays cheap
  const searchIndex = useMemo<SearchEntry[]>(
    () =>
      projects.map((project) => ({
        id: String(project.id),
        label: projectLabel(project),
        clientName: project.clientName || '',
        haystack: [project.name, project.billingCode, project.clientName, project.projectManager]
          .filter(Boolean)
          .join(' ')
          .toLowerCase(),
      })),
    [projects]
  );

  const labelsById = useMemo(() => {
    const map = new Map<string, string>();
    searchIndex.forEach((entry) => map.set(entry.id, entry.label));
    return map;
  }, [searchIndex]);

  // Every whitespace-separated term must appear, so "acme migration" matches across fields
  const matches = useMemo(() => {
    const terms = debouncedQuery.split(/\s+/).filter(Boolean);
    if (terms.length === 0) return [];
    return searchIndex.filter((entry) => terms.every((term) => entry.haystack.includes(term)));
  }, [searchIndex, debouncedQuery]);

  const visibleMatches = useMemo(
    () => matches.slice(0, MAX_VISIBLE_RESULTS).sort((a, b) => a.label.localeCompare(b.label)),
    [matches]
  );

  const selectedEntries = useMemo(
    () =>
      value.map((id) => ({
        id,
        label: id === NO_PROJECT_VALUE ? 'No Project assigned' : labelsById.get(id) || id,
      })),
    [value, labelsById]
  );

  const toggle = useCallback(
    (id: string) => {
      if (value.includes(id)) {
        onValueChange(value.filter((item) => item !== id));
        return;
      }
      if (id !== NO_PROJECT_VALUE) recordProjectAccess(id);
      onValueChange([...value, id]);
    },
    [value, onValueChange, recordProjectAccess]
  );

  const favouriteIds = useMemo(
    () => new Set(favouriteProjects.map((project) => String(project.id))),
    [favouriteProjects]
  );

  const suggestions = useMemo(
    () => recentProjects.filter((project) => !favouriteIds.has(String(project.id))),
    [recentProjects, favouriteIds]
  );

  const renderRow = (
    id: string,
    label: string,
    subLabel?: string,
    options?: { favouritable?: boolean }
  ) => (
    <CommandItem
      key={id}
      value={id}
      onSelect={() => toggle(id)}
      className="px-3 py-2 cursor-pointer"
    >
      <div className="flex items-center gap-2 w-full">
        <Checkbox checked={value.includes(id)} className="shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-sm truncate block">{label}</span>
          {subLabel && (
            <span className="text-xs text-muted-foreground truncate block">{subLabel}</span>
          )}
        </div>
        {options?.favouritable && (
          <Star
            className={cn(
              'h-3.5 w-3.5 shrink-0 cursor-pointer transition-colors',
              isFavourite(id)
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-muted-foreground hover:text-yellow-400'
            )}
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              toggleFavourite(id);
            }}
          />
        )}
      </div>
    </CommandItem>
  );

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div
            role="button"
            aria-expanded={open}
            className={cn(
              'border-input w-full flex items-center justify-between min-h-10 h-auto py-2 px-3 text-sm',
              'border rounded-md bg-background cursor-pointer',
              'hover:bg-accent hover:text-accent-foreground',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
            )}
          >
            <div className="flex flex-wrap items-center gap-1 flex-1 min-w-0 pr-2">
              {selectedEntries.length === 0 ? (
                <span className="text-muted-foreground">{placeholder}</span>
              ) : (
                <>
                  {selectedEntries.slice(0, MAX_VISIBLE_CHIPS).map((entry) => (
                    <div key={entry.id} onClick={(event) => event.stopPropagation()}>
                      <Badge
                        variant="secondary"
                        onClose={() => onValueChange(value.filter((id) => id !== entry.id))}
                      >
                        {entry.label}
                      </Badge>
                    </div>
                  ))}
                  {selectedEntries.length > MAX_VISIBLE_CHIPS && (
                    <Badge variant="outline">+{selectedEntries.length - MAX_VISIBLE_CHIPS} more</Badge>
                  )}
                </>
              )}
            </div>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </PopoverTrigger>

        <PopoverContent className="w-[380px] p-0" align="start" sideOffset={4} collisionPadding={10}>
          <Command shouldFilter={false}>
            <div className="flex items-center border-b px-3">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <input
                autoFocus
                placeholder="Search by name, code, client or PM..."
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                className="flex h-10 w-full bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>

            <CommandList className="max-h-[320px] overflow-y-auto">
              {selectedEntries.length > 0 && (
                <>
                  <CommandGroup>
                    <div className="flex items-center justify-between px-3 py-2">
                      <span className="text-xs font-semibold uppercase text-muted-foreground">
                        Selected ({selectedEntries.length})
                      </span>
                      <button
                        type="button"
                        className="text-xs text-primary hover:underline"
                        onClick={() => onValueChange([])}
                      >
                        Clear all
                      </button>
                    </div>
                    {(showAllSelected
                      ? selectedEntries
                      : selectedEntries.slice(0, MAX_COLLAPSED_SELECTED)
                    ).map((entry) => renderRow(entry.id, entry.label))}
                    {selectedEntries.length > MAX_COLLAPSED_SELECTED && (
                      <button
                        type="button"
                        className="w-full px-3 py-2 text-left text-xs text-primary hover:underline"
                        onClick={() => setShowAllSelected((previous) => !previous)}
                      >
                        {showAllSelected
                          ? 'Show fewer'
                          : `Show all ${selectedEntries.length} selected`}
                      </button>
                    )}
                  </CommandGroup>
                  <Separator />
                </>
              )}

              {debouncedQuery ? (
                matches.length === 0 ? (
                  <div className="py-6 text-center">
                    <Briefcase className="mx-auto h-8 w-8 text-muted-foreground/50 mb-2" />
                    <p className="text-sm text-muted-foreground">No projects found</p>
                    <p className="text-xs text-muted-foreground mt-1">Try a different search term</p>
                  </div>
                ) : (
                  <CommandGroup>
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      {matches.length > MAX_VISIBLE_RESULTS
                        ? `Showing ${MAX_VISIBLE_RESULTS} of ${matches.length.toLocaleString()} matches — refine your search`
                        : `${matches.length} match${matches.length !== 1 ? 'es' : ''}`}
                    </div>
                    {visibleMatches
                      .filter((entry) => !value.includes(entry.id))
                      .map((entry) =>
                        renderRow(entry.id, entry.label, entry.clientName, { favouritable: true })
                      )}
                  </CommandGroup>
                )
              ) : (
                <>
                  {!value.includes(NO_PROJECT_VALUE) && (
                    <CommandGroup>
                      {renderRow(NO_PROJECT_VALUE, 'No Project assigned')}
                    </CommandGroup>
                  )}

                  {favouriteProjects.length > 0 && (
                    <CommandGroup>
                      <div className="flex items-center gap-2 px-3 py-2">
                        <Star className="h-3.5 w-3.5 text-yellow-500" />
                        <span className="text-xs font-semibold uppercase text-muted-foreground">
                          Favourites
                        </span>
                      </div>
                      {favouriteProjects
                        .filter((project) => !value.includes(String(project.id)))
                        .map((project) =>
                          renderRow(
                            String(project.id),
                            projectLabel(project),
                            project.clientName,
                            { favouritable: true }
                          )
                        )}
                    </CommandGroup>
                  )}

                  {suggestions.length > 0 && (
                    <CommandGroup>
                      <div className="flex items-center gap-2 px-3 py-2">
                        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-xs font-semibold uppercase text-muted-foreground">
                          Recently used
                        </span>
                      </div>
                      {suggestions
                        .filter((project) => !value.includes(String(project.id)))
                        .map((project) =>
                          renderRow(
                            String(project.id),
                            projectLabel(project),
                            project.clientName,
                            { favouritable: true }
                          )
                        )}
                    </CommandGroup>
                  )}

                  <p className="px-3 pb-3 pt-1 text-xs text-muted-foreground">
                    {projects.length.toLocaleString()} projects available — search above or browse them
                    all.
                  </p>
                </>
              )}
            </CommandList>

            <div className="border-t p-2">
              <Button
                variant="ghost"
                className="w-full justify-center text-sm"
                onClick={() => {
                  setOpen(false);
                  setBrowseOpen(true);
                }}
              >
                <ArrowRight className="mr-2 h-4 w-4" />
                Browse all projects...
              </Button>
            </div>
          </Command>
        </PopoverContent>
      </Popover>

      <ProjectSearchModal
        open={browseOpen}
        onOpenChange={setBrowseOpen}
        projects={enrichedProjects}
        onSelect={() => undefined}
        onToggleFavourite={toggleFavourite}
        isFavourite={isFavourite}
        configuredStatuses={statuses}
        multiple
        selectedProjectIds={value.filter((id) => id !== NO_PROJECT_VALUE)}
        onSelectionChange={(ids) =>
          onValueChange(value.includes(NO_PROJECT_VALUE) ? [NO_PROJECT_VALUE, ...ids] : ids)
        }
      />
    </>
  );
}
